import { DoorStatus, DoorType, DoorUsage } from "./enums";
import { XGSSelectOption } from "../../../ui-components/xgs-select/xgsSelect";
import { SelectOption } from "../equipment/models";

export interface Door {
  terminalNumber?: number;
  doorNumber?: string;
  doorType?: DoorType;
  primaryDistributor?: string;
  doorPriority?: string;
  trailerNumber?: number;
  manifestNumber?: number;
  doorUsage?: DoorUsage;
  doorStatus?: DoorStatus;
}

export enum DoorTypeCode {
  CITY_DELIVERY = "C",
  LOADING = "L",
  OVERAGES = "O",
  RACK = "R",
  STAGING = "S",
  SHORTAGES = "T",
  UNLOADING = "U"
}

export enum DoorUsageCode {
  CITY_RACK = "C",
  LOCAL_ONLY = "L",
  MOBILE_STG = "M",
  NON_PERM = "N",
  OVERAGE = "O",
  PERMANENT = "P",
  WMS = "Q",
  SHORTAGE = "T",
  WILL_CALLS = "W",
  LOST = "Z"
}

export interface SecondaryDistributor {
  id: string;
  accountNumber: string;
  name: string;
  attention: string;
  address: string;
}

export interface AssignDoorModalProps {
  show: boolean;
  onClose: () => void;
  onSave?: (data: {
    door: string;
    carrier: string;
    trailer: string;
    priority: string;
    primaryDistributor: string;
    secondaryDistributors: SecondaryDistributor[];
    doorUsage: string;
    doorType?: string;
  }) => void;
  onBack?: () => void;
  manifestNumber?: string;
  originTerminal?: string;
  destinationTerminal?: string;
  shipmentCount?: number;
}

export interface AssignDoorFormValues {
  door: XGSSelectOption | null;
  priority: XGSSelectOption | null;
  primaryDistributor: XGSSelectOption | null;
  carrier: XGSSelectOption | null;
  trailer: XGSSelectOption | null;
  doorUsage: XGSSelectOption | null;
  doorType: XGSSelectOption | null;
}

export interface AddSecondaryDistributorModalProps {
  show: boolean;
  onClose: () => void;
  onAdd: (distributor: SecondaryDistributor) => void;
  existingDistributorIds: string[];
}

export const PRIORITY_OPTIONS: XGSSelectOption[] = [
  { label: "Medium", value: "MEDIUM" },
  { label: "High", value: "HIGH" },
  { label: "Critical", value: "CRITICAL" },
];

export const DOOR_TYPE_OPTIONS: XGSSelectOption[] = [
  { label: "City Delivery", value: DoorTypeCode.CITY_DELIVERY },
  { label: "Loading", value: DoorTypeCode.LOADING },
  { label: "Overages", value: DoorTypeCode.OVERAGES },
  { label: "Rack", value: DoorTypeCode.RACK },
  { label: "Staging", value: DoorTypeCode.STAGING },
  { label: "Shortages", value: DoorTypeCode.SHORTAGES },
  { label: "Unloading", value: DoorTypeCode.UNLOADING },
];

export const DOOR_USAGE_OPTIONS: XGSSelectOption[] = [
  { label: "City Rack", value: DoorUsageCode.CITY_RACK },
  { label: "Local Only", value: DoorUsageCode.LOCAL_ONLY },
  { label: "Mobile Staging", value: DoorUsageCode.MOBILE_STG },
  { label: "Non Permanent", value: DoorUsageCode.NON_PERM },
  { label: "Overage", value: DoorUsageCode.OVERAGE },
  { label: "Permanent", value: DoorUsageCode.PERMANENT },
  { label: "WMS", value: DoorUsageCode.WMS },
  { label: "Shortage", value: DoorUsageCode.SHORTAGE },
  { label: "Will Calls", value: DoorUsageCode.WILL_CALLS },
  { label: "Lost", value: DoorUsageCode.LOST },
];

export const statusOptions: SelectOption[] = [
  { label: "Unassigned", value: "UNASSIGNED" },
  { label: "Loading", value: "LOADING" },
  { label: "Closed", value: "CLOSED" },
  { label: "Departed", value: "DEPARTED" }
];

export const TIME_PERIOD_LIST: {
  [key: string]: string;
} = {
  TODAY: "Today",
  YESTERDAY: "Yesterday",
  TOMORROW: "Tomorrow",
  THIS_WEEK: "This Week",
  LAST_WEEK: "Last Week",
  NEXT_WEEK: "Next Week"
};

export interface LoadboardListManifestRequestParams {
  manifestOriginTerminalNumber?: number[] | string[] | string | number;
  startCutDate?: Date | string;
  endCutDate?: Date | string;
  doorManifestStatus?: string[] | string;
  carrierNumber?: number;
  doorNumber?: string;
  manifestNumberSearch?: string;
  manifestDestinationTerminalNumber?: number;
}

export interface LoadboardListManifestTrip {
  carrierNumber: string;
  carrierName: string;
  destinationTerminalAirportCode: string;
  destinationTerminalNumber: any;
  destinationTerminalName: string;
  doorNumber: string;
  doorManifestStatus: string;
  driverId: string;
  driverName: string;
  originTerminalNumber: any;
  arrivalDate: any;

  // --- Manifest-level schedule (parent row Phase 1: origin + final destination) ---
  /** Est arrival at final destination (`FINAL_DEST_DUE_DATE`). */
  finalDestinationDueDate?: string | null;
  finalDestinationDueTime?: string | null;
  /** Est departure from origin (also used per-leg Est Departure on sub-trips). */
  estimatedDepartureDate?: string | null;
  estimatedDepartureTime?: string | null;
  /** Actual departure from origin. */
  originActualDepartureDate?: string | null;
  originActualDepartureTime?: string | null;
  /** Actual arrival at final destination. */
  finalDestinationActualArrivalDate?: string | null;
  finalDestinationActualArrivalTime?: string | null;

  // --- Leg-level schedule (expand sub-trip Phase 2) ---
  legEstArrivalDate?: string | null;
  legEstArrivalTime?: string | null;
  legActualArrivalDate?: string | null;
  legActualArrivalTime?: string | null;
  legActualDepartureDate?: string | null;
  legActualDepartureTime?: string | null;
  legOriginDropType?: string | null;
  legDestinationDropType?: string | null;

  dispatchTime: any;
  dispatchDate: any;
  manifestNumber: string | number;
  manifestId: string;
  door: string;
  status: string;
  destination: string;
  loadPercent: number;
  weight: number;
  pieces: number;
  eta: string;
  driver?: string;
  trailer?: string;
  plannedCloseDate?: string;
  plannedCloseTime?: string;
  originTerminalAirportCode: string;
  originTerminalName: string;
  trailerNumber: string;
  tractorNumber: string;
  tripNumber: number;
  carrier: string;
}

/** POST body for `/load-board/manifests/{id}/dispatch`. `dispatchedDateTime` is ISO-8601 in UTC (e.g. `Date.prototype.toISOString()`). */
export interface LoadBoardDispatchLoadRequest {
  tractorNumber: string | null;
  trailerNumber: string | null;
  sealNumber: number | null;
  lhPro: number | null;
  carrierNumber: string | null;
  dispatchedDateTime: string;
}

export interface LoadboardManifestShipmentStatus {
  title: string;
  description: string;
}

export interface LoadboardManifestShipmentTerminal {
  terminalNumber: number;
  terminalName: string;
  airportCode?: string | null;
}

export interface LoadboardManifestShipmentPod {
  date?: string | null;
  signature?: string | null;
}

export interface LoadboardManifestShipment {
  probillNumber: number;
  poNumber?: string | null;
  bolNumber?: string | null;
  billDate?: string | null;
  status?: LoadboardManifestShipmentStatus | null;
  origin?: LoadboardManifestShipmentTerminal | null;
  destination?: LoadboardManifestShipmentTerminal | null;
  etaDate?: string | null;
  rate?: number | null;
  pod?: LoadboardManifestShipmentPod | null;
  rollCount?: number | null;
  palletCount?: number | null;
  huCount?: number | null;
  totalWeight?: number | null;
}

/** GET `/load-board/manifests/{manifestNumber}/shipments` */
export interface LoadboardManifestShipmentsResponse {
  manifestNumber: number;
  rollCount: number;
  palletCount: number;
  huCount: number;
  totalWeight: number;
  shipments: LoadboardManifestShipment[];
}